import { UserProfileRole } from '@@types/api.types';
import { UserProfileComponentProps } from '@components/pages/home/components/home-user-profile-modal.component.tsx';
import { ErrorMessage } from '@components/shared/error-message.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { SelectionCard } from '@components/shared/selection-card.component';
import { cn } from '@lib/utils';
import { SUPPORT_EMAIL } from '@utils/constants';
import { BrainIcon, CpuIcon, GraduationCapIcon } from 'lucide-react';
import { FunctionComponent } from 'react';

export interface RoleStepProps extends UserProfileComponentProps {
  isRoleDisabled?: boolean;
}

const roles = [
  {
    value: UserProfileRole.INNOVATOR,
    icon: (isSelected: boolean) => (
      <CpuIcon className={cn('size-8', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
    primaryLabel: 'Innovator',
    secondaryLabel: 'Manage and evaluate your technologies.',
  },
  {
    value: UserProfileRole.EXPERT,
    icon: (isSelected: boolean) => (
      <GraduationCapIcon className={cn('size-8', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
    primaryLabel: 'Expert',
    secondaryLabel: 'Help innovators assess their technologies.',
  },
  {
    value: UserProfileRole.INTELLIGENCE,
    icon: (isSelected: boolean) => (
      <BrainIcon className={cn('size-7', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
    primaryLabel: 'Intelligence',
    secondaryLabel: 'Collaborate with innovators and garner insights.',
  },
];

export const RoleStep: FunctionComponent<RoleStepProps> = (props) => {
  const { formData, setFormData, errors, isRoleDisabled } = props;

  const handleRoleSelect = (role: UserProfileRole) => {
    setFormData((prevData) => ({
      ...prevData,
      role,
    }));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row items-center justify-center pb-6 gap-4">
        {roles.map(({ value, icon, primaryLabel, secondaryLabel }) => (
          <SelectionCard
            key={value}
            value={value}
            icon={icon}
            primaryLabel={primaryLabel}
            secondaryLabel={secondaryLabel}
            isSelected={formData.role === value}
            onClick={() => handleRoleSelect(value)}
            onKeyUp={(e) => !isRoleDisabled && (e.key === 'Enter' || e.key === ' ') && handleRoleSelect(value)}
            isDisabled={isRoleDisabled}
          />
        ))}
      </div>
      <ErrorMessage zodError={errors.role} />
      {isRoleDisabled && (
        <p className="flex items-center gap-1 mx-auto text-sm text-center text-gray-600">
          To change your role, please contact{' '}
          <ExternalLink
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-blue-600"
            showIcon={false}
          >
            {SUPPORT_EMAIL}
          </ExternalLink>
        </p>
      )}
    </div>
  );
};
